import { inject } from '@angular/core';
import { CanActivateFn, RedirectCommand, Router } from '@angular/router';

import { NAV_ENTRIES } from '../../navigation/navigation.config';
import { authorizedHomePath } from '../session/landing-redirect';
import { type AppRole } from '../session/session.model';
import { SessionService } from '../session/session.service';
import { AuthTelemetry } from './auth-telemetry';

/**
 * Resolves a destination's required roles from the same role map that drives
 * NAV_ENTRIES — the single source of truth, so navigation projection and
 * route authorization cannot drift (R-107). Nested client-detail URLs share
 * the /clients entry; unknown destinations resolve to null.
 */
export function requiredRolesForPath(url: string): readonly AppRole[] | null {
  const path = url.split('?')[0].split('#')[0];
  // AITH-45 U4 removed the temporary dev-harness carve-out with the harnesses themselves. Any
  // surviving /dev/* URL now resolves to null and therefore fails closed, which is the documented
  // default rather than a new behaviour.
  const navPath = path.startsWith('/clients') ? '/clients' : path;
  return NAV_ENTRIES.find((entry) => entry.path === navPath)?.requiredRoles ?? null;
}

/**
 * Route authorization (FR-010, FR-011): composed after sessionGuard on every
 * protected destination (order is load-bearing — authentication before
 * authorization). Permits on any-of role match; otherwise cancels with a
 * RedirectCommand to the session's authorized home and records exactly one
 * unauthorizedNavigation event.
 *
 * Structural control, not a security boundary — enforcement is API-side
 * (AITH-20/21). Unknown destinations fail closed (deny + home redirect).
 */
export const roleGuard: CanActivateFn = (_route, state) => {
  const session = inject(SessionService);
  const required = requiredRolesForPath(state.url);
  if (required !== null && session.hasAnyRole(required)) return true;

  const roles = session.session().roles;
  const home = authorizedHomePath(roles);
  const category = roles.some((role) => role.startsWith('Client')) ? 'client' : 'staff';
  inject(AuthTelemetry).unauthorizedNavigation(category);
  return new RedirectCommand(inject(Router).createUrlTree([home]));
};
